import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  X, Save, Trophy, CheckCircle2, XCircle, Clock, Crown, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { NATIONALITIES } from "@/lib/nationalities";

/**
 * Full-history dialog for an Eliminatórias user (admin side).
 * Tabs: Perfil (editable) · Eliminatórias (per-elim summary) · Tentativas (raw log).
 */
export default function EliminatoriasUserDialog({ userId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/eliminatorias/users/${userId}/history`);
      setData(data);
      setForm({
        name: data.user.name || "",
        school: data.user.school || "",
        nationality: data.user.nationality || "PT",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/eliminatorias/users/${userId}`, {
        name: (form.name || "").trim() || null,
        school: (form.school || "").trim() || null,
        nationality: form.nationality || null,
      });
      toast.success("Perfil atualizado");
      onSaved?.();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="elim-user-dialog" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-violet-200 font-bold">Eliminatórias Escolares</div>
            <h2 className="font-[Fredoka] text-xl font-bold">
              {data?.user?.username || "—"}{data?.user?.name ? ` · ${data.user.name}` : ""}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/15 rounded-full p-1.5" data-testid="elim-user-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-12 text-center text-slate-500">A carregar…</div>
        ) : (
          <Tabs defaultValue="profile" className="p-6">
            <TabsList className="grid grid-cols-3 w-full bg-slate-100 rounded-full p-1 mb-6">
              <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="udlg-tab-profile">Perfil</TabsTrigger>
              <TabsTrigger value="eliminatorias" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="udlg-tab-eliminatorias">Eliminatórias ({data.per_eliminatoria.length})</TabsTrigger>
              <TabsTrigger value="attempts" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="udlg-tab-attempts">Tentativas ({data.attempts.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <form onSubmit={saveProfile} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Username</Label>
                    <Input value={data.user.username} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={data.user.email} disabled className="bg-slate-50" />
                  </div>
                </div>
                <div>
                  <Label>Nome</Label>
                  <Input data-testid="udlg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Escola</Label>
                    <Input data-testid="udlg-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} />
                  </div>
                  <div>
                    <Label>Nacionalidade</Label>
                    <Select value={form.nationality} onValueChange={(v) => setForm({ ...form, nationality: v })}>
                      <SelectTrigger data-testid="udlg-nationality"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {NATIONALITIES.map((n) => <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(() => {
                  const quals = data.finalist_qualifications || [];
                  const isQualified = (gameId, tierId) =>
                    quals.some((q) => q.game_id === gameId && (q.tier_id || null) === (tierId || null));
                  // Distinct (game, escalão) the student actually participated in.
                  const partMap = new Map();
                  (data.per_eliminatoria || []).forEach((r) => {
                    const key = `${r.game_id}||${r.tier_id || ""}`;
                    if (!partMap.has(key)) {
                      partMap.set(key, {
                        game_id: r.game_id, game_name: r.game_name,
                        tier_id: r.tier_id, tier_name: r.tier_name,
                      });
                    }
                  });
                  const participations = [...partMap.values()];
                  // Also surface finalist qualifications that have no attempt row
                  // (e.g. legacy), so a finalist is never hidden.
                  quals.forEach((q) => {
                    const key = `${q.game_id}||${q.tier_id || ""}`;
                    if (!partMap.has(key)) {
                      participations.push({ game_id: q.game_id, game_name: q.game_name, tier_id: q.tier_id, tier_name: q.tier_name });
                      partMap.set(key, true);
                    }
                  });
                  if (participations.length === 0 && quals.length === 0) return null;
                  return (
                    <div className="rounded-xl bg-slate-50 border-2 border-slate-200 p-3 space-y-2" data-testid="udlg-participation">
                      <div className="text-xs uppercase tracking-wider text-slate-600 font-bold flex items-center gap-1.5">
                        <Crown className="w-4 h-4 text-amber-500" /> Participação por escalão / nível
                      </div>
                      <div className="space-y-1.5">
                        {participations.map((p) => {
                          const qualified = isQualified(p.game_id, p.tier_id);
                          return (
                            <div key={`${p.game_id}-${p.tier_id || "none"}`} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`udlg-part-${p.game_id}-${p.tier_id || "none"}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-semibold text-slate-800 truncate">{p.game_name || "—"}</span>
                                <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold whitespace-nowrap">
                                  {p.tier_name || "Sem escalão"}
                                </span>
                              </div>
                              {qualified ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold whitespace-nowrap">
                                  <Trophy className="w-3 h-3" /> Apurou à final
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold whitespace-nowrap">
                                  Não apurou
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between pt-2 text-xs text-slate-500">
                  <span>Criado: {data.user.created_at ? new Date(data.user.created_at).toLocaleString() : "—"}</span>
                  <span>Último acesso: {data.user.last_seen_at ? new Date(data.user.last_seen_at).toLocaleString() : "—"}</span>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
                  <Button type="submit" disabled={saving} data-testid="udlg-save" className="bg-violet-600 hover:bg-violet-700 rounded-full">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "A guardar…" : "Guardar"}
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="eliminatorias">
              {data.per_eliminatoria.length === 0 ? (
                <div className="text-center text-slate-400 py-10" data-testid="udlg-no-elim">
                  Este utilizador ainda não tentou nenhuma eliminatória.
                </div>
              ) : (() => {
                // Compute sequential rank within each game so it matches what the
                // student sees in the lobby (independent of the raw `order` value).
                const rankByElim = {};
                const byGame = new Map();
                data.per_eliminatoria.forEach((r) => {
                  if (!byGame.has(r.game_id)) byGame.set(r.game_id, []);
                  byGame.get(r.game_id).push(r);
                });
                for (const arr of byGame.values()) {
                  arr.sort((a, b) => (a.order || 0) - (b.order || 0));
                  arr.forEach((r, i) => { rankByElim[r.id] = i + 1; });
                }
                return (
                <table className="w-full text-sm" data-testid="udlg-eliminatorias-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Jogo</th>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Eliminatória</th>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-32">Escalão/Nível</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-24">Tentativas</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-20">Alvo</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-28">Melhor tempo</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-24">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_eliminatoria.map((row) => {
                      const left = Math.max(0, (row.max_attempts || 0) - row.attempts);
                      return (
                        <tr key={row.id} className="border-b border-slate-100" data-testid={`udlg-elim-row-${row.id}`}>
                          <td className="px-3 py-2 text-slate-700">{row.game_name || <em className="text-slate-400">—</em>}</td>
                          <td className="px-3 py-2 text-slate-700">
                            <div className="font-semibold">#{rankByElim[row.id] || ((row.order ?? 0) + 1)}</div>
                            <div className="text-xs text-slate-500">{row.name}</div>
                          </td>
                          <td className="px-3 py-2">
                            {row.tier_name ? (
                              <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold">{row.tier_name}</span>
                            ) : <span className="text-slate-300 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="font-bold text-slate-900">{row.attempts}</span>
                            <span className="text-slate-400"> / {row.max_attempts}</span>
                            {left > 0 && !row.passed && (
                              <div className="text-[10px] text-emerald-600 font-bold">{left} restantes</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-700">
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" />{row.target_time}s</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.best_time != null ? (
                              <span className={`font-mono font-bold ${row.passed ? "text-emerald-600" : "text-slate-700"}`}>
                                {Number(row.best_time).toFixed(1)}s
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.passed ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                                <CheckCircle2 className="w-3 h-3" /> Passada
                              </span>
                            ) : left === 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                                <XCircle className="w-3 h-3" /> Esgotada
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                                Em curso
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                );
              })()}
            </TabsContent>

            <TabsContent value="attempts">
              {data.attempts.length === 0 ? (
                <div className="text-center text-slate-400 py-10" data-testid="udlg-no-attempts">
                  Sem tentativas registadas.
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[420px]" data-testid="udlg-attempts-table">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-200">
                        <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Data</th>
                        <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Jogo</th>
                        <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Eliminatória</th>
                        <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-24">Tempo</th>
                        <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-20">Alvo</th>
                        <th className="text-center px-3 py-2 text-xs font-bold uppercase text-slate-600 w-24">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.attempts.map((a) => (
                        <tr key={a.id} className="border-b border-slate-100" data-testid={`udlg-attempt-row-${a.id}`}>
                          <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                            {a.finished_at ? new Date(a.finished_at).toLocaleString() : (a.started_at ? new Date(a.started_at).toLocaleString() + " ⏳" : "—")}
                          </td>
                          <td className="px-3 py-2 text-slate-700">{a.game_name || "—"}</td>
                          <td className="px-3 py-2 text-slate-700">
                            <div className="font-semibold text-xs">#{(a.eliminatoria_order ?? 0) + 1}</div>
                            <div className="text-[10px] text-slate-500">{a.eliminatoria_name}</div>
                            {a.tier_name && <div className="text-[10px] text-violet-600 font-bold">{a.tier_name}</div>}
                          </td>
                          <td className="px-3 py-2 text-center font-mono">
                            {a.time_seconds != null ? `${Number(a.time_seconds).toFixed(1)}s` : "—"}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">{a.target_time}s</td>
                          <td className="px-3 py-2 text-center">
                            {a.status === "in_progress" ? (
                              <span className="text-amber-600 text-xs font-bold">Em curso</span>
                            ) : a.passed ? (
                              <Trophy className="w-4 h-4 text-emerald-500 inline" />
                            ) : (
                              <XCircle className="w-4 h-4 text-rose-500 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end pt-3">
                <Button onClick={load} variant="outline" size="sm" data-testid="udlg-reload">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Recarregar
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
